import React, { useMemo, useState } from 'react';
import { GitCompare, Save, Plus, FileText, RotateCcw, Trash2, ArrowLeftRight } from 'lucide-react';
import { useBoqBillVersions, MILESTONE_KINDS, STAGE_BY_KIND } from '../hooks/useBoqBillVersions';
import {
  BrioCard, BrioButton, BrioEmptyState, BrioModal, BrioModalBody, BrioModalField, BrioInput, BrioSelect,
} from '../../components/BrioComponents';
import { formatMoney, formatQty } from '../utils/money';
import { renderBillHtml, openBillForPrint } from '../utils/renderBillHtml';
import { ToolbarIcon } from './ToolbarIcon';
import { StickyScrollBox } from './StickyScrollbar';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '');
const lineKey = (l) => `${l.itemVersionId || l.fullCode}|${l.projectZoneId || ''}`;

function StageChip({ kind }) {
  const stage = STAGE_BY_KIND[kind];
  if (!stage || !stage.short) return null;
  return (
    <span title={stage.long} style={{ marginLeft: 8, fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.03em', background: 'var(--brio-gold, #9F902A)', color: '#fff', borderRadius: 3, padding: '0 5px' }}>
      {stage.short}
    </span>
  );
}

function NewMilestoneModal({ onClose, onCreate }) {
  const [name, setName] = useState('Estimate');
  const [kind, setKind] = useState('estimate');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const submit = async () => {
    if (!name.trim()) { setErr('Name is required.'); return; }
    setBusy(true);
    const r = await onCreate({ name, kind, label });
    setBusy(false);
    if (r.success) onClose(); else setErr(r.error || 'Failed.');
  };
  return (
    <BrioModal title="New milestone" onClose={onClose} width="sm" footer={
      <>
        <BrioButton variant="secondary" onClick={onClose} disabled={busy}>Cancel</BrioButton>
        <BrioButton variant="primary" onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create + snapshot'}</BrioButton>
      </>
    }>
      <BrioModalBody form>
        {err && <div style={{ color: '#a94442', marginBottom: '0.5rem' }}>{err}</div>}
        <BrioModalField label="Kind">
          <BrioSelect value={kind} onChange={(e) => { setKind(e.target.value); const k = MILESTONE_KINDS.find((x) => x.value === e.target.value); if (k) setName(k.label); }}>
            {MILESTONE_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
          </BrioSelect>
        </BrioModalField>
        <BrioModalField label="Name" hint="Shows in the version code, e.g. KU_BOQ-Estimate-1.1_…">
          <BrioInput value={name} onChange={(e) => setName(e.target.value)} />
        </BrioModalField>
        <BrioModalField label="Note (optional)">
          <BrioInput value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. issued to client" />
        </BrioModalField>
      </BrioModalBody>
    </BrioModal>
  );
}

function SaveRevisionModal({ milestone, onClose, onSave }) {
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => { setBusy(true); const r = await onSave(label); setBusy(false); if (r.success) onClose(); };
  return (
    <BrioModal title={`Save revision under ${milestone.name}`} onClose={onClose} width="sm" footer={
      <>
        <BrioButton variant="secondary" onClick={onClose} disabled={busy}>Cancel</BrioButton>
        <BrioButton variant="primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Save revision'}</BrioButton>
      </>
    }>
      <BrioModalBody form>
        <BrioModalField label="Note (optional)" hint="What changed in this revision?">
          <BrioInput value={label} onChange={(e) => setLabel(e.target.value)} autoFocus placeholder="e.g. added external works" />
        </BrioModalField>
      </BrioModalBody>
    </BrioModal>
  );
}

function CompareModal({ a, b, fetchVersionLines, currency, onClose }) {
  const [rows, setRows] = useState(null);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [la, lb] = await Promise.all([fetchVersionLines(a.id), fetchVersionLines(b.id)]);
        if (cancelled) return;
        const mapA = new Map(la.map((l) => [lineKey(l), l]));
        const mapB = new Map(lb.map((l) => [lineKey(l), l]));
        const keys = [...new Set([...mapA.keys(), ...mapB.keys()])];
        const diff = keys.map((k) => {
          const x = mapA.get(k); const y = mapB.get(k);
          return {
            key: k,
            code: (y || x).fullCode,
            desc: (y || x).description,
            unit: (y || x).unit,
            qtyA: x?.quantity ?? null, qtyB: y?.quantity ?? null,
            amtA: x?.amountQuoted ?? 0, amtB: y?.amountQuoted ?? 0,
            status: !x ? 'added' : !y ? 'removed' : 'changed',
          };
        }).filter((d) => d.status !== 'changed' || d.qtyA !== d.qtyB || d.amtA !== d.amtB);
        const tA = la.reduce((s, l) => s + (l.amountQuoted || 0), 0);
        const tB = lb.reduce((s, l) => s + (l.amountQuoted || 0), 0);
        setRows(diff);
        setTotals({ a: tA, b: tB });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [a.id, b.id, fetchVersionLines]);

  return (
    <BrioModal title={`Compare — estimate basis`} onClose={onClose} width="xl" footer={<BrioButton variant="secondary" onClick={onClose}>Close</BrioButton>}>
      <BrioModalBody>
        <div style={{ fontSize: '0.85rem', marginBottom: '0.5rem' }}>
          <strong>A</strong> <code>{a.code}</code> &nbsp;vs&nbsp; <strong>B</strong> <code>{b.code}</code>
        </div>
        {loading ? <div style={{ color: 'var(--brio-text-muted)' }}>Loading…</div> : (
          <>
            {totals && (
              <div style={{ marginBottom: '0.6rem', fontSize: '0.9rem' }}>
                Total A {formatMoney(totals.a, currency)} → B {formatMoney(totals.b, currency)} ·
                <strong style={{ color: totals.b - totals.a >= 0 ? '#2e7d32' : '#a94442', marginLeft: 6 }}>
                  {totals.b - totals.a >= 0 ? '+' : ''}{formatMoney(totals.b - totals.a, currency)}
                </strong>
              </div>
            )}
            <StickyScrollBox style={{ maxHeight: 440, overflow: 'auto', border: '1px solid var(--brio-border, #ccc)', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead style={{ position: 'sticky', top: 0, background: 'var(--brio-surface-muted, #f7f7f5)' }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Code</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Description</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Qty A</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Qty B</th>
                    <th style={{ padding: '0.4rem 0.5rem', textAlign: 'right' }}>Δ Amount</th>
                    <th style={{ padding: '0.4rem 0.5rem' }}>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 ? (
                    <tr><td colSpan={6} style={{ padding: '0.75rem', color: 'var(--brio-text-muted)' }}>No differences.</td></tr>
                  ) : rows.map((d) => {
                    const dAmt = d.amtB - d.amtA;
                    const colour = d.status === 'added' ? '#2e7d32' : d.status === 'removed' ? '#a94442' : 'inherit';
                    return (
                      <tr key={d.key} style={{ borderTop: '1px solid var(--brio-border-subtle, #eee)' }}>
                        <td style={{ padding: '0.35rem 0.5rem', fontFamily: 'monospace' }}>{d.code}</td>
                        <td style={{ padding: '0.35rem 0.5rem' }}>{d.desc}</td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{d.qtyA == null ? '—' : formatQty(d.qtyA)}</td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{d.qtyB == null ? '—' : formatQty(d.qtyB)}</td>
                        <td style={{ padding: '0.35rem 0.5rem', textAlign: 'right' }}>{dAmt ? formatMoney(dAmt, currency) : '—'}</td>
                        <td style={{ padding: '0.35rem 0.5rem', color: colour, fontWeight: d.status !== 'changed' ? 600 : 400 }}>
                          {d.status === 'added' ? 'new in B' : d.status === 'removed' ? 'only in A' : 'changed'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </StickyScrollBox>
          </>
        )}
      </BrioModalBody>
    </BrioModal>
  );
}

function VersionsTab({ project, api: apiProp, onAfterChange }) {
  // Prefer a shared hook instance from the page (keeps the active-stage state in
  // sync with the header switcher); fall back to a local instance if not given.
  const localApi = useBoqBillVersions(apiProp ? null : project.boqId);
  const api = apiProp || localApi;
  const [showNewMilestone, setShowNewMilestone] = useState(false);
  const [saveUnder, setSaveUnder] = useState(null);
  const [showAuto, setShowAuto] = useState(false);
  const [selected, setSelected] = useState([]); // up to 2 version ids for compare
  const [compare, setCompare] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const cur = project.currency || 'INR';

  const versionsByMilestone = useMemo(() => {
    const m = new Map();
    api.versions.forEach((v) => {
      if (v.is_auto && !showAuto) return;
      if (!m.has(v.milestone_id)) m.set(v.milestone_id, []);
      m.get(v.milestone_id).push(v);
    });
    m.forEach((arr) => arr.sort((a, b) => b.revision_seq - a.revision_seq));
    return m;
  }, [api.versions, showAuto]);

  const toggleSelect = (id) => setSelected((prev) => {
    if (prev.includes(id)) return prev.filter((x) => x !== id);
    return [...prev, id].slice(-2);
  });

  const handleRestore = async (v) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Restore revision ${v.code} into the live bill? The current state is auto-snapshotted first.`)) return;
    setBusyId(v.id);
    const r = await api.restore(v.id);
    setBusyId(null);
    if (r.success) { onAfterChange?.(); }
    // eslint-disable-next-line no-alert
    else window.alert(r.error || 'Restore failed.');
  };

  const handleSwitch = async (m) => {
    const fromName = api.activeMilestone ? `${api.activeMilestone.name}` : 'the current stage';
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Switch the live bill to "${m.name}"?\n\nYour work under "${fromName}" is saved first, and you can switch back any time.`)) return;
    setBusyId(m.id);
    const r = await api.switchStage(m.id);
    setBusyId(null);
    if (r.success) { onAfterChange?.(); }
    // eslint-disable-next-line no-alert
    else window.alert(r.error || 'Switch failed.');
  };

  const handlePdf = async (v) => {
    const lines = await api.fetchVersionLines(v.id);
    const html = renderBillHtml(lines, { projectName: project.name, projectCode: project.projectCode, currency: cur, basis: 'quoted' });
    openBillForPrint(html);
  };

  const selectedVersions = selected.map((id) => api.versions.find((v) => v.id === id)).filter(Boolean);

  if (!project.boqId) {
    return <BrioCard><BrioEmptyState>Set up BOQ for this project to start versioning.</BrioEmptyState></BrioCard>;
  }

  return (
    <BrioCard>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0, fontFamily: 'Raleway, sans-serif', fontSize: '1.05rem' }}>Versions</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: '0.8rem', color: 'var(--brio-text-muted)', display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
            <input type="checkbox" checked={showAuto} onChange={(e) => setShowAuto(e.target.checked)} /> Show autosaves
          </label>
          {selectedVersions.length === 2 && (
            <BrioButton variant="secondary" onClick={() => setCompare({ a: selectedVersions[0], b: selectedVersions[1] })}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
              <GitCompare size={14} /> Compare 2
            </BrioButton>
          )}
          <BrioButton variant="secondary" onClick={() => setSaveUnder(api.activeMilestone || api.latestMilestone)} disabled={!(api.activeMilestone || api.latestMilestone)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
            <Save size={14} /> Save revision
          </BrioButton>
          <BrioButton variant="primary" onClick={() => setShowNewMilestone(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.35rem 0.7rem', fontSize: '0.82rem' }}>
            <Plus size={14} /> New milestone
          </BrioButton>
        </div>
      </div>

      {api.error && <div style={{ color: '#a94442', marginBottom: '0.5rem' }}>{api.error}</div>}

      {api.milestones.length === 0 ? (
        <BrioEmptyState>
          <div style={{ marginBottom: '0.5rem' }}>No versions yet.</div>
          <div style={{ fontSize: '0.82rem', marginBottom: '0.75rem' }}>Create your first milestone (Estimate) to snapshot the current bill.</div>
          <BrioButton variant="primary" onClick={() => setShowNewMilestone(true)}>New milestone</BrioButton>
        </BrioEmptyState>
      ) : (
        api.milestones.slice().sort((a, b) => a.seq - b.seq).map((m) => {
          const revs = versionsByMilestone.get(m.id) || [];
          const isActive = api.activeMilestoneId === m.id;
          const hasAnyVersion = api.versions.some((v) => v.milestone_id === m.id);
          return (
            <div key={m.id} style={{ marginBottom: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', borderBottom: '2px solid var(--brio-gold, #9F902A)', paddingBottom: '0.25rem', marginBottom: '0.4rem' }}>
                <div style={{ fontWeight: 700, fontFamily: 'Raleway, sans-serif' }}>
                  {m.seq}. {m.name}
                  <StageChip kind={m.kind} />
                  <span style={{ fontWeight: 400, fontSize: '0.8rem', color: 'var(--brio-text-muted)', marginLeft: 6 }}>· {m.kind}</span>
                </div>
                {isActive ? (
                  <span title="The live bill currently shows this stage" style={{ fontSize: '0.72rem', fontWeight: 700, color: '#2e7d32', background: '#2e7d3215', border: '1px solid #2e7d3240', borderRadius: 4, padding: '0.1rem 0.45rem', whiteSpace: 'nowrap' }}>
                    ● Active (live)
                  </span>
                ) : hasAnyVersion ? (
                  <BrioButton variant="secondary" onClick={() => handleSwitch(m)} disabled={busyId === m.id}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', padding: '0.2rem 0.55rem', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    <ArrowLeftRight size={13} /> {busyId === m.id ? 'Switching…' : 'Switch to this stage'}
                  </BrioButton>
                ) : null}
              </div>
              {revs.length === 0 ? (
                <div style={{ fontSize: '0.82rem', color: 'var(--brio-text-muted)', paddingLeft: '1rem' }}>No revisions shown.</div>
              ) : revs.map((v) => (
                <div key={v.id} style={{ display: 'grid', gridTemplateColumns: '24px 1fr auto', gap: '0.5rem', alignItems: 'center', padding: '0.35rem 0 0.35rem 1rem', borderBottom: '1px solid var(--brio-border-subtle, #eee)' }}>
                  <input type="checkbox" checked={selected.includes(v.id)} onChange={() => toggleSelect(v.id)} title="Select to compare (max 2)" />
                  <div>
                    <code style={{ fontSize: '0.8rem' }}>{v.code}</code>
                    {v.is_auto && <span style={{ marginLeft: 6, fontSize: '0.68rem', background: '#eee', borderRadius: 3, padding: '0 4px', color: 'var(--brio-text-muted)' }}>auto</span>}
                    <span style={{ marginLeft: 8, fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>{v.label || ''} · {fmtDate(v.snapshot_at)}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '0.1rem', alignItems: 'center' }}>
                    <ToolbarIcon onClick={() => handlePdf(v)} tooltip="Printable bill (PDF)">
                      <FileText size={15} />
                    </ToolbarIcon>
                    {isActive && (
                      <ToolbarIcon onClick={() => handleRestore(v)} disabled={busyId === v.id} tooltip="Restore this revision (revert within stage)">
                        <RotateCcw size={15} />
                      </ToolbarIcon>
                    )}
                    <ToolbarIcon onClick={() => api.deleteVersion(v.id)} danger tooltip="Delete version">
                      <Trash2 size={15} />
                    </ToolbarIcon>
                  </div>
                </div>
              ))}
            </div>
          );
        })
      )}

      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--brio-text-muted)' }}>
        Each milestone is a stage (SD / TD / GFC …). <strong>Switch to this stage</strong> makes another stage the live bill — your current stage is saved first, so nothing is lost and you can switch back. <strong>Restore this revision</strong> reverts within the active stage. "Save revision" snapshots the live bill; "New milestone" starts a new stage from the current bill. Tick two versions to compare.
      </div>

      {showNewMilestone && (
        <NewMilestoneModal onClose={() => setShowNewMilestone(false)} onCreate={api.newMilestoneWithSnapshot} />
      )}
      {saveUnder && (
        <SaveRevisionModal milestone={saveUnder} onClose={() => setSaveUnder(null)} onSave={(label) => api.snapshot(saveUnder.id, label, false)} />
      )}
      {compare && (
        <CompareModal a={compare.a} b={compare.b} fetchVersionLines={api.fetchVersionLines} currency={cur} onClose={() => setCompare(null)} />
      )}
    </BrioCard>
  );
}

export default VersionsTab;
